import { type AuthUser } from 'wasp/auth';
import { FolderTree, Building2, Layers, Grid3x3, Wrench, Tag, BookOpen, Settings, ArrowRight, Map, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';

const TaxonomyPage = ({ user }: { user: AuthUser }) => {
  const navigate = useNavigate();

  const taxonomyCategories = [
    {
      icon: MapPin,
      title: "Location Names",
      description: "Create and manage locations in the system",
      path: "/admin/taxonomy/location-names",
      color: "text-teal-600",
      bgColor: "bg-teal-50"
    },
    {
      icon: Map,
      title: "Location Categories",
      description: "Define types of locations in the system",
      path: "/admin/taxonomy/location-categories",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50"
    },
    {
      icon: Building2,
      title: "Organization Types",
      description: "Define types of organizations in the system",
      path: "/admin/organization-types",
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: Grid3x3,
      title: "Organization Sections",
      description: "Configure platform sections and their properties",
      path: "/admin/sections",
      color: "text-indigo-600",
      bgColor: "bg-indigo-50"
    },
    {
      icon: Wrench,
      title: "Service Names",
      description: "Define services available in the platform",
      path: "/admin/service-definitions",
      color: "text-violet-600",
      bgColor: "bg-violet-50"
    },
    {
      icon: Tag,
      title: "Topic Categories",
      description: "Manage types of topics for categorization",
      path: "/admin/topic-types",
      color: "text-fuchsia-600",
      bgColor: "bg-fuchsia-50"
    },
    {
      icon: BookOpen,
      title: "Topic Names",
      description: "Configure topics used throughout the platform",
      path: "/admin/topics",
      color: "text-pink-600",
      bgColor: "bg-pink-50"
    },
    {
      icon: Settings,
      title: "Field Configurations",
      description: "Manage field visibility and requirements",
      path: "/admin/field-configs",
      color: "text-rose-600",
      bgColor: "bg-rose-50"
    }
  ];

  return (
    <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <FolderTree className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-content-text">Taxonomy Configuration</h1>
          </div>
          <p className="text-content-text-muted">
            Manage all taxonomies and classification systems used throughout the platform.
            Select a category from the sidebar or click a card below to get started.
          </p>
        </div>

        {/* Category Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {taxonomyCategories.map((category) => {
            const Icon = category.icon;
            return (
              <Card
                key={category.path}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(category.path)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className={`p-3 rounded-lg ${category.bgColor}`}>
                      <Icon className={`h-6 w-6 ${category.color}`} />
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400" />
                  </div>
                  <CardTitle className="mt-4">{category.title}</CardTitle>
                  <CardDescription>{category.description}</CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </div>
  );
};

export default TaxonomyPage;



